import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthState } from '@/components/feature/AuthGuard';
import { useQiRole } from '../lib/useQiRole';

const PATH_STEPS = [
  { key: 'heb1', label: 'RG／HEB1 築基', desc: '施炁者與準 Practitioner 進入子場前的重要築基與資格路徑。' },
  { key: 'qualification', label: '資格申請與覆核', desc: '完成築基後提出申請，經 ABAA 覆核並正式授權。' },
  { key: 'og', label: 'OG 一之門', desc: '生命實踐與見證系統的入口，接駁子場實作。' },
  { key: 'subfield', label: '子場實作', desc: '在子場中進行宇炁實作，記錄施炁與受施過程。' },
  { key: 'qi', label: '宇炁療育', desc: '記錄施炁、受施者第一視角回報與持續追蹤，建立可覆核的 Evidence。' },
];

export default function OgPortalPage() {
  const auth = useAuthState();
  const { role } = useQiRole(auth.user);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isLoggedIn = !!auth.user;

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-sm border-b border-foreground-100/60'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <nav className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap cursor-pointer">
            <span className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
              <i className="ri-flashlight-line text-sm text-background-50" />
            </span>
            <span className="text-base tracking-wide text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              OG 一之門
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/og/qi-healing"
              className="whitespace-nowrap px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-foreground-50 rounded-md transition-colors cursor-pointer"
            >
              宇炁療育
            </Link>
            <Link
              to="/og/qi-healing/witnesses"
              className="whitespace-nowrap px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-foreground-50 rounded-md transition-colors cursor-pointer"
            >
              見證牆
            </Link>
            <Link
              to="/"
              className="whitespace-nowrap px-4 py-2 text-sm rounded-md bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer ml-1"
            >
              回到首頁
            </Link>
          </div>
        </nav>
      </header>

      <main className="pb-20">
        {/* 全幅生命樹：與首頁黃金生命樹相同，不受內容寬度限制 */}
        <section className="relative isolate flex min-h-[820px] w-full items-center justify-center overflow-hidden bg-[#e9dcfa] px-5 pb-20 pt-28 text-center md:min-h-[920px] md:px-10 md:pt-36">
          <img src="/images/trilogy/og-tree.jpg" alt="一之門階段的淡紫生命樹" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/35 via-white/10 to-white/75" />
          <div className="mx-auto max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/75 px-3 py-1 text-xs text-accent-700">
              <i className="ri-flashlight-line" />
              OG 一之門 &middot; Oneness Gate
            </span>
            <h1 className="mt-6 text-3xl text-foreground-900 md:text-5xl" style={{ fontFamily: 'var(--font-heading)' }}>
              OG 一之門簡介
            </h1>
            <p className="mx-auto mt-7 max-w-2xl rounded-xl bg-white/60 px-5 py-4 text-sm leading-relaxed text-foreground-800 backdrop-blur-sm md:text-base">
              從淡紫生命樹出發：個體實踐 HEB123、宇炁與衷愛軸，集體建造子場、母場，創新價值。此 Portal 記錄施炁、受施者回報及持續追蹤，建立可覆核、可補充、可撤回的 Evidence。
            </p>
            <a href="#og-path" className="mt-8 inline-block rounded-md bg-white/85 px-6 py-3 text-sm text-foreground-900 shadow-sm hover:bg-white">從生命樹開始探索</a>
          </div>
        </section>

        <div className="mx-auto max-w-5xl space-y-16 px-4 pt-16 md:px-6">
          <section id="og-path" className="scroll-mt-24 text-center">

            {/* 路徑流程 */}
            <div className="bg-background-100 border border-foreground-200/50 rounded-2xl p-6 md:p-8">
              <h2 className="text-base font-medium text-foreground-800 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                進入路徑
              </h2>
              <div className="flex flex-col md:flex-row items-stretch gap-3 md:gap-0">
                {PATH_STEPS.map((step, idx) => (
                  <div key={step.key} className="flex-1 flex flex-row md:flex-col items-center gap-3 md:gap-2">
                    <div className="flex items-center gap-3 md:flex-col md:gap-2 w-full">
                      <span className="shrink-0 w-9 h-9 rounded-full bg-accent-500 text-background-50 text-sm flex items-center justify-center font-medium">
                        {idx + 1}
                      </span>
                      <div className="text-left md:text-center">
                        <p className="text-sm font-medium text-foreground-800 whitespace-nowrap md:whitespace-normal">{step.label}</p>
                        <p className="text-xs text-foreground-500 mt-1 hidden md:block">{step.desc}</p>
                      </div>
                    </div>
                    {idx < PATH_STEPS.length - 1 && (
                      <i className="ri-arrow-down-line md:ri-arrow-right-line text-foreground-300 text-lg rotate-90 md:rotate-0 self-center md:self-start md:mt-4" />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-foreground-500 mt-6 md:hidden leading-relaxed">
                完成 RG／HEB1 築基、經 ABAA 覆核授權後，即可進入 OG 子場，進行宇炁實作並留下可覆核的見證。
              </p>
            </div>
          </section>

          {/* 三個主要按鈕 */}
          <section className="space-y-5">
            <h2 className="text-lg text-foreground-800 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              選擇你的入口
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link
                to="/og/qi-healing"
                className="group flex flex-col items-start gap-3 bg-background-100 border border-foreground-200/50 rounded-xl p-5 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-heart-line text-xl text-primary-700" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">我是受施者／填寫回報</p>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                    接收施炁者邀請、確認同意、填寫第一視角回報與追蹤。
                  </p>
                </div>
              </Link>

              <Link
                to="/og/qi-healing"
                className="group flex flex-col items-start gap-3 bg-background-100 border border-foreground-200/50 rounded-xl p-5 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-add-line text-xl text-accent-700" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">施炁者登入／建立案例</p>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                    具有資格者登入後建立宇炁案例、填寫施炁紀錄與邀請受施者。
                  </p>
                </div>
              </Link>

              <Link
                to="/og/qi-healing/witnesses"
                className="group flex flex-col items-start gap-3 bg-background-100 border border-foreground-200/50 rounded-xl p-5 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-eye-line text-xl text-secondary-900" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">查看匿名見證</p>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                    瀏覽已核准公開、去識別化的宇炁見證。
                  </p>
                </div>
              </Link>
            </div>

            {/* 次要入口 */}
            <div className="text-center pt-1">
              <a
                href="#heb1-path"
                className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-foreground-500 hover:text-accent-600 cursor-pointer transition-colors"
              >
                <i className="ri-information-line" />
                了解如何由 RG／HEB1 進入子場
              </a>
            </div>
          </section>

          {/* 施炁者 vs 受施者 */}
          <section id="heb1-path" className="scroll-mt-24 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-background-100 border border-foreground-200/50 rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center">
                  <i className="ri-flashlight-line text-lg text-background-50" />
                </span>
                <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                  對施炁者
                </h2>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                完成 RG／HEB1 是進入 OG 子場的重要築基，但<span className="text-foreground-900 font-medium">不代表自動取得 Practitioner 資格</span>。
              </p>
              <div className="space-y-2.5">
                <div className="bg-background-50 border border-foreground-200/50 rounded-lg p-4">
                  <p className="text-sm font-medium text-foreground-800">準 Practitioner</p>
                  <p className="text-xs text-foreground-500 mt-1">資格仍在觀察與審定中。</p>
                </div>
                <div className="bg-background-50 border border-foreground-200/50 rounded-lg p-4">
                  <p className="text-sm font-medium text-foreground-800">Practitioner</p>
                  <p className="text-xs text-foreground-500 mt-1">已完成 ABAA 覆核並正式授權。</p>
                </div>
              </div>
              <p className="text-xs text-foreground-500 leading-relaxed border-t border-foreground-200/50 pt-3">
                系統權限與專業資格必須持續解耦，不可因為是會員或完成 HEB1 就自動取得施炁權限。
              </p>
            </div>

            <div className="bg-background-100 border border-foreground-200/50 rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center">
                  <i className="ri-user-heart-line text-lg text-background-50" />
                </span>
                <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                  對受施者
                </h2>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                受施者<span className="text-foreground-900 font-medium">不必先完成 HEB1</span>，經施炁者邀請即可參與。
              </p>
              <ul className="space-y-2 text-sm text-foreground-600">
                {[
                  '接收施炁者發出的 Email 邀請。',
                  '打開專屬安全連結。',
                  '確認同意。',
                  '填寫施炁前、當下及施炁後回報。',
                  '上傳照片、影片及文件並逐檔決定授權。',
                  '繼續填寫 7／30／90 日追蹤。',
                  '管理見證公開與撤回權利。',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-check-line text-accent-500 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 登入後：個人入口 */}
          {isLoggedIn && (
            <section className="bg-background-100 border border-foreground-200/50 rounded-2xl p-6 space-y-4">
              <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                我的入口
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/og/qi-healing"
                  className="whitespace-nowrap px-4 py-2.5 rounded-md bg-background-50 border border-foreground-200 text-sm text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  我的宇炁紀錄
                </Link>
                {(role === 'practitioner' || role === 'candidate') && (
                  <Link
                    to="/og/qi-healing/new"
                    className="whitespace-nowrap px-4 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    施炁者工作台
                  </Link>
                )}
                {(role === 'admin' || role === 'reviewer') && (
                  <Link
                    to="/og/qi-healing/review"
                    className="whitespace-nowrap px-4 py-2.5 rounded-md bg-foreground-100 text-foreground-700 text-sm hover:bg-foreground-200 transition-colors cursor-pointer"
                  >
                    Admin／Reviewer 後台
                  </Link>
                )}
              </div>
            </section>
          )}

          {/* 公開聲明 */}
          <section className="bg-accent-100/50 border border-accent-200 rounded-2xl p-6 space-y-3">
            <div className="flex items-start gap-3">
              <i className="ri-shield-check-line text-accent-600 text-xl mt-0.5" />
              <div className="space-y-2">
                <h2 className="text-base font-medium text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  公開聲明
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  本平台用於宇炁實踐、個人經驗回報與見證記錄，不取代醫療診斷、治療或緊急醫療服務。身體不適者仍應諮詢合格醫療專業人員。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  案例主訴、前後變化與影像均以當事人回報為準，系統不會自動改寫成醫療療效結論。
                </p>
              </div>
            </div>
          </section>
          <section aria-label="一之門階段目標" className="relative isolate flex min-h-[320px] items-end overflow-hidden rounded-2xl md:min-h-[430px]">
            <img src="/images/trilogy/og-gate.jpg" alt="通往一之門的紫色門" loading="lazy" className="absolute inset-0 -z-20 h-full w-full object-cover" />
            <div className="absolute inset-0 -z-10 bg-gradient-to-t from-white/95 via-white/25 to-transparent" />
            <p className="mx-auto max-w-2xl px-5 pb-8 text-center text-base text-foreground-900 md:text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
              個體與場域共同成熟，走向 6.5D 一之門；資格仍依 Evidence 與覆核標準，不會因付款或登錄而自動取得。
            </p>
          </section>
          <section className="text-center">
            <p className="text-sm leading-7 text-foreground-600">想了解《一之門》、宇炁見證系統與 ABAA 長年生命工作的源流？</p>
            <Link to="/abaa-witness" className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-accent-700 hover:text-accent-800">
              閱讀 ABAA 生命經歷與任務見證
              <i className="ri-arrow-right-line" />
            </Link>
          </section>
        </div>
      </main>
    </div>
  );
}
